import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './YearPicker.module.scss';

type Props = {
  value: string;
  onChange: (year: string) => void;
  years?: number[];
  allLabel?: string;
}

const YearPicker: FC<Props> = ({
  value,
  onChange,
  years: yearsProp = [],
  allLabel = 'Alla',
}) => {
  const [t] = useTranslation('shared', { keyPrefix: 'yearPicker' });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const years = [...yearsProp].sort((a, b) => b - a);
  const currentIndex = value ? years.indexOf(parseInt(value)) : -1;

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handlePrev = () => {
    const next = currentIndex + 1;
    if (next < years.length) onChange(String(years[next]));
  };

  const handleNext = () => {
    const prev = currentIndex - 1;
    if (prev >= 0) onChange(String(years[prev]));
  };

  const prevDisabled = !value || currentIndex === years.length - 1;
  const nextDisabled = !value || currentIndex === 0;

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.control}>
        <button className={styles.arrow} onClick={handlePrev} disabled={prevDisabled}>
          &#8249;
        </button>
        <button className={styles.display} onClick={() => years.length > 0 ? setOpen(o => !o) : undefined}>
          {value || allLabel}
        </button>
        <button className={styles.arrow} onClick={handleNext} disabled={nextDisabled}>
          &#8250;
        </button>
      </div>
      {open && years.length > 0 && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownLabel}>{t('filterByYear', 'Filtrera efter år')}</div>
          <div className={styles.grid}>
            {years.map(year => (
              <button
                key={year}
                className={`${styles.option} ${value === String(year) ? styles.active : ''}`}
                onClick={() => { onChange(String(year)); setOpen(false); }}
              >
                {year}
              </button>
            ))}
            <button
              className={`${styles.option} ${!value ? styles.active : ''}`}
              onClick={() => { onChange(''); setOpen(false); }}
            >
              {allLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default YearPicker;
